/**
 * Surprav Storefront API Helper
 * Simplifies GraphQL queries to the Shopify Storefront API.
 * Integrates with StateStore for TTL-caching and request deduplication.
 */

class StorefrontAPI {
  constructor() {
    this.token = window.__surprav?.storefrontToken;
    this.shop = window.__surprav?.shop;
    this.endpoint = `https://${this.shop}/api/2026-01/graphql.json`;
  }

  /**
   * Generate a deterministic cache key from a GraphQL query and variables.
   * @param {string} graphqlQuery
   * @param {Object} variables
   * @returns {string}
   */
  _cacheKey(graphqlQuery, variables) {
    const normalized = graphqlQuery.replace(/\s+/g, ' ').trim();
    return 'sfapi:' + normalized + ':' + JSON.stringify(variables);
  }

  /**
   * Fetch with exponential backoff for HTTP 429 (Too Many Requests).
   * Delays: 1s, 2s, 4s — max 3 retries.
   * @param {string} url
   * @param {RequestInit} options
   * @param {number} [retries=3]
   * @returns {Promise<Response>}
   * @private
   */
  async _fetchWithBackoff(url, options, retries = 3) {
    let attempt = 0;
    while (true) {
      const response = await fetch(url, options);
      if (response.status === 429 && attempt < retries) {
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        await new Promise((resolve) => setTimeout(resolve, delay));
        attempt++;
        continue;
      }
      return response;
    }
  }

  /**
   * Execute a raw GraphQL query against the Storefront API.
   * Uses exponential backoff for HTTP 429 responses.
   * @param {string} graphqlQuery
   * @param {Object} [variables={}]
   * @returns {Promise<Object|null>}
   */
  async query(graphqlQuery, variables = {}) {
    if (!this.token) {
      console.warn('Storefront API token is missing.');
      return null;
    }

    try {
      const response = await this._fetchWithBackoff(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: variables
        })
      });

      if (!response.ok) {
        throw new Error(`Storefront API error: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL Errors:', result.errors);
        return null;
      }

      return result.data;
    } catch (error) {
      console.error('Storefront API fetch error:', error);
      return null;
    }
  }

  /**
   * Execute a GraphQL query with TTL-caching via StateStore.
   * Falls back to a direct query() call when StateStore is unavailable.
   * @param {string} graphqlQuery
   * @param {Object} [variables={}]
   * @param {number} [ttl=60000] — cache TTL in milliseconds
   * @returns {Promise<Object|null>}
   */
  async cachedQuery(graphqlQuery, variables = {}, ttl = 60000) {
    const store = window.Surprav?.StateStore;
    if (!store || typeof store.cachedFetch !== 'function') {
      return this.query(graphqlQuery, variables);
    }

    const key = this._cacheKey(graphqlQuery, variables);
    try {
      return await store.cachedFetch(key, () => this.query(graphqlQuery, variables), ttl);
    } catch (error) {
      console.error('Storefront API cached query error:', error);
      return null;
    }
  }

  /**
   * Fetch home feed configuration from App API.
   */
  async getFeedConfig() {
    const appUrl = window.__surprav?.appUrl;
    if (!appUrl) return null;
    
    try {
      const res = await fetch(`${appUrl}/api/feed`);
      return await res.json();
    } catch (e) {
      console.error('Failed to fetch feed config', e);
      return null;
    }
  }

  /**
   * Fetch trending products with CJ metadata using Storefront GraphQL.
   * Uses cachedQuery for automatic TTL-caching.
   */
  async getTrendingProducts(limit = 8) {
    const query = `
      query TrendingProducts($first: Int!) {
        products(first: $first, sortKey: BEST_SELLING) {
          nodes {
            id
            title
            handle
            featuredImage { url altText }
            priceRange { minVariantPrice { amount currencyCode } }
            cj_order_count: metafield(namespace: "surprav", key: "cj_order_count") { value }
            cj_lists_count: metafield(namespace: "surprav", key: "cj_lists_count") { value }
          }
        }
      }
    `;
    const data = await this.cachedQuery(query, { first: limit });
    return data?.products?.nodes || [];
  }

  /**
   * Predictive search for products and collections.
   * Uses cachedQuery for automatic TTL-caching.
   */
  async predictiveSearch(query, limit = 6) {
    const SEARCH_QUERY = `
      query PredictiveSearch($query: String!, $limit: Int!) {
        predictiveSearch(query: $query, limit: $limit, types: [PRODUCT, COLLECTION]) {
          products {
            id
            title
            handle
            featuredImage {
              url(transform: { maxWidth: 120, maxHeight: 120, crop: CENTER })
              altText
            }
            priceRange {
              minVariantPrice { amount currencyCode }
            }
          }
          collections {
            id
            title
            handle
            image {
              url(transform: { maxWidth: 80 })
            }
          }
        }
      }
    `;

    const data = await this.cachedQuery(SEARCH_QUERY, { query, limit });
    return data ? data.predictiveSearch : null;
  }
}

window.SurpravAPI = new StorefrontAPI();
